import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { notification, NOTIFICATION_TYPES } from '_atoms';
import { getTodaysInterviews } from 'store/selectors/jobSelectors';
import { setTodaysnterviews } from 'store/reducers/jobReducer';
import type { TodaysInterview } from 'store/reducers/jobReducer';
import { getTodaysInterviewsAPI } from 'actions';

const TODAYS_INTERVIEWS_SYNC_KEY = 'ats:todaysInterviews:sync';

type AppThunkDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

const InterviewsCheck = () => {
  const dispatch = useDispatch<AppThunkDispatch>();

  const interviews = useSelector(getTodaysInterviews) as TodaysInterview[] | undefined;

  const loadInterviews = useCallback(() => {
    dispatch(getTodaysInterviewsAPI());
  }, [dispatch]);

  useEffect(() => {
    loadInterviews();
    const interval = setInterval(loadInterviews, 6 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadInterviews]);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === TODAYS_INTERVIEWS_SYNC_KEY) loadInterviews();
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [loadInterviews]);

  useEffect(() => {
    const checkInterviews = () => {
      const now = moment();
      const toNotify =
        interviews?.filter(interview => {
          const start = moment(interview.interview_start_datetime);
          const minutesUntilStart = start.diff(now, 'minutes', true);
          return minutesUntilStart >= 0 && minutesUntilStart < 1;
        }) || [];

      if (toNotify.length === 0) return;

      (dispatch(getTodaysInterviewsAPI()) as unknown as Promise<TodaysInterview[]>).then(freshList => {
        const notifiedUuids = new Set(toNotify.map(i => i.uuid));
        dispatch(setTodaysnterviews((freshList || []).filter(i => !notifiedUuids.has(i.uuid))));
        toNotify.forEach(interview => {
          notification({
            message: (
              <>
                It&#39;s time for the interview with{' '}
                <a
                  href={`/jobs/${interview.job}/show/steps/${interview.step}/${interview.candidate.uuid}`}
                  onClick={() => {}}
                >
                  {interview.candidate.full_name}
                </a>{' '}
                to start. Please confirm it
              </>
            ),
            type: NOTIFICATION_TYPES.SUCCESS,
            duration: 9999999,
            onClick: (e: React.MouseEvent<HTMLElement>) => {
              const target = e.target as HTMLElement;
              if (target.tagName.toLowerCase() === 'a')
                target
                  .closest('.ant-notification')
                  ?.querySelector<HTMLElement>('.ant-notification-notice-close')
                  ?.click();
            },
          });
        });
      });
    };

    const checkInterval = setInterval(checkInterviews, 60 * 1000);
    return () => clearInterval(checkInterval);
  }, [interviews, dispatch]);

  return null;
};

export default InterviewsCheck;
